import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, takeWhile } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { AppState } from '../../reducers';
import { LayoutUtilsService } from '../../_base/crud';
import { NoteService } from '../_services';
import {
  selectNoteActionLoading,
  selectNoteError,
  selectNoteQuery,
} from '../_selectors/note.selector';
import {
  CancelNotePage,
  ClearNoteError,
  CreateNote,
  CreateNoteOnServer,
  DisplayNoteMessage,
  LoadNotePage,
  NoteActionTypes,
  RequestNotePage,
  ThrowNoteError,
  ToggleNoteActionLoading,
  ToggleNotePageLoading,
  UpdateNote,
  UpdateNoteOnServer,
} from '../_actions/note.action';

@Injectable()
export class NoteEffect {
  showPageLoading = new ToggleNotePageLoading({ pageLoading: true });
  hidePageLoading = new ToggleNotePageLoading({ pageLoading: false });
  showActionLoading = new ToggleNoteActionLoading({ actionLoading: true });
  hideActionLoading = new ToggleNoteActionLoading({ actionLoading: false });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private noteService: NoteService,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
  ) {}

  @Effect()
  loadNotePage$ = this.actions$.pipe(
    ofType<RequestNotePage>(NoteActionTypes.RequestNotePage),
    map((action: RequestNotePage) => action.payload),
    mergeMap(({ query }) => {
      this.store.dispatch(this.showPageLoading);
      const queryResult$ = this.noteService.getNotePage(query);
      const observables = [queryResult$, of(query)];

      return forkJoin(observables).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.length) {
            this.store.dispatch(new ClearNoteError());
            this.store.dispatch(this.hidePageLoading);

            return new LoadNotePage({
              notes: res[0].data,
              totalCount: res[0].count,
              query: res[1],
            });
          }

          this.store.dispatch(new CancelNotePage());

          return new ThrowNoteError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  createNote$ = this.actions$.pipe(
    ofType<CreateNoteOnServer>(NoteActionTypes.CreateNoteOnServer),
    map((action: CreateNoteOnServer) => action.payload),
    mergeMap(({ note, close }) => {
      this.store.dispatch(this.showActionLoading);

      return this.noteService.createNote(note).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 201) {
            close({ note });

            this.store.dispatch(new ClearNoteError());
            this.store.dispatch(this.hideActionLoading);

            return new CreateNote({ note: res.body?.data });
          }

          close();

          return new ThrowNoteError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateNote$ = this.actions$.pipe(
    ofType<UpdateNoteOnServer>(NoteActionTypes.UpdateNoteOnServer),
    map((action: UpdateNoteOnServer) => action.payload),
    mergeMap(({ note, close }) => {
      this.store.dispatch(this.showActionLoading);

      return this.noteService.updateNote(note).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            close({ note });

            this.store.dispatch(new ClearNoteError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateNote({ note: res.body?.data });
          }

          close();

          return new ThrowNoteError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  displayNoteMessage$ = this.actions$.pipe(
    ofType<DisplayNoteMessage>(NoteActionTypes.DisplayNoteMessage),
    map((action: DisplayNoteMessage) => action.payload),
    mergeMap(({ message, messageType }) => {
      return this.store.pipe(
        select(selectNoteActionLoading),
        takeWhile((isLoading) => isLoading === true),
        finalize(() => {
          this.store.pipe(select(selectNoteError)).subscribe((err) => {
            if (err) {
              const status = err.status;

              if (status === 401 || status === 403 || status === 404) return;

              message = this.translate.instant('GLOBAL.UNSPECIFIED_ERROR');
              return this.layoutUtils.showActionNotification(
                message,
                messageType,
                3000,
                false,
                false,
              );
            }

            this.layoutUtils.showActionNotification(message, messageType, 3000, false, false);

            if (localStorage.getItem('fromNoteList')) {
              this.store
                .pipe(select(selectNoteQuery))
                .subscribe((query) => this.store.dispatch(new RequestNotePage({ query })));
            }
          });
        }),
      );
    }),
  );
}
